import React, { useState, useMemo, useCallback } from "react";
import WidgetCard from "./WidgetCard";
import { buildWidgetCardFilters } from "@/ts/revstack/ui-utils";
import { Row, Col } from "antd";
import {
    CA_DD_MODULES,
    CommonWidgetMetrics,
    OptionValues,
    RevstackCASectionIds,
    RevstackCubeIds,
    RevstackDashboardModules,
    RevstackFilterKey,
    RevstackFilterTypes,
    WidgetConfig,
    WidgetData,
    WidgetType,
} from "@ts/common-types";
import { useDispatch } from "react-redux";
import { getRevstackSelectedFilterOptions } from "@/ts/revstack/filters";
import { trendsClickHandler } from "@/ts/revstack/constants";

interface IRevstackPoCategoryAnalysisWidget {
    widgetData: WidgetData;
    widgetConfig: {
        widgets: WidgetConfig[][];
        formatter: (widget: WidgetConfig, widgetData: WidgetData) => any;
        config: {
            trendsFilters: any[];
            hideTrendsTabSection: boolean;
            defaultTab: CA_DD_MODULES;
            module: RevstackDashboardModules;
            trendsBtnSectionId: RevstackCASectionIds;
            isTrendsButtonRequired: boolean;
            isTrendsButtonComingSoon: boolean;
            showCategorySelect?: boolean;
            showRegionSelect?: boolean;
            widgetCard: {
                type: string;
                isBodyPaddingReq: boolean;
            };
        };
    };
    title: string;
}


//  Calculates the maximum number of drill down rows from formatted widget data
//  Used to ensure consistent height for no-data sections
const calculateMaxDDRowsForWidgets = (
    formattedWidgets: Array<{ widget: any; componentData: any }>
): number => {
    return Math.max(...formattedWidgets.map(({ widget, componentData }) => {
        const drilldownData = componentData?.drilldownData || [];
        const limit = widget?.config?.drilldown?.limit;
        if (!drilldownData || drilldownData.length === 0) return 0;
        return limit ? Math.min(limit, drilldownData.length) : drilldownData.length;
    }), 0);
};

//! Note: this component itself can be reduced into a hook while refactoring for the grids
const RevstackPoCategoryAnalysisWidget: React.FC<
    IRevstackPoCategoryAnalysisWidget
> = ({ widgetConfig, widgetData, title }) => {
    const [hoveredCard, setHoveredCard] = useState<boolean>(false);
    const dispatch = useDispatch();
    const handleMouseEnter = useCallback(() => setHoveredCard(true), []);
    const handleMouseLeave = useCallback(() => setHoveredCard(false), []);
    // Get selected filter options using utility function
    const selectedFilterOptions = getRevstackSelectedFilterOptions();

    const renderWidget = useCallback(
        (widget: WidgetConfig, index: number, cachedComponentData?: any) => {
            // Use cached data if provided, otherwise call formatter
            const componentData = cachedComponentData ?? widget.formatter(widget, widgetData);
            
            if (widget.type === WidgetType.W1) {
                return (
                    <Col xs={24} md={8} key={`${widget.type}-${index}`}>
                        <widget.widgetComponent
                            title={widget.title}
                            subTitle={widget?.subTitle || ""}
                            infoMessage={widget.infoMessage || ""}
                            config={widget.config}
                            {...componentData}
                        />
                    </Col>
                );
            }

            if (widget.type === WidgetType.W2) {
                return (
                    <Col xs={24} md={8} key={`${widget.type}-${index}`}>
                        <widget.widgetComponent
                            title={widget.title}
                            icon={widget.icon}
                            config={widget.config}
                            {...componentData}
                        />
                    </Col>
                );
            }

            return null;
        },
        [widgetData]
    );

    // Recursive function to render widget structures of any nesting level
    const renderWidgetStructure = useCallback(
        (structure: any, keyPrefix = ""): any => {
            if (Array.isArray(structure)) {
                // Check if all elements are widgets (objects with type property)
                const allAreWidgets = structure.every(item => 
                    item && typeof item === 'object' && item.type && !Array.isArray(item)
                );
                
                if (allAreWidgets) {
                    // This is a row of widgets - render them in a Row with Cols
                    // First, call formatter for all widgets
                    const formattedWidgets = structure.map((widget: any) => ({
                        widget,
                        componentData: widget.formatter(widget, widgetData)
                    }));
                    
                    // Calculate max rows from formatted data
                    const maxRowsInRow = calculateMaxDDRowsForWidgets(formattedWidgets);

                    return (
                        <Row
                            gutter={[20, 20]}
                            className="mt-4"
                            key={`row-${keyPrefix}`}
                        >
                            {formattedWidgets.map(({ widget, componentData }, widgetIndex) => {
                                const widgetWithMaxRows = {
                                    ...widget,
                                    config: {
                                        ...widget.config,
                                        maxRowsInGroup: maxRowsInRow > 0 ? maxRowsInRow : undefined
                                    }
                                };
                                return renderWidget(widgetWithMaxRows, widgetIndex, componentData);
                            })}
                        </Row>
                    );
                } else {
                    // Check for the specific pattern: [nested_grid, single_widgets...]
                    // This handles cases like [[[w1,w2],[w3,w4]], [w5]]
                    const hasNestedGridPattern = structure.some(item => 
                        Array.isArray(item) && item.some(subItem => Array.isArray(subItem))
                    );
                    
                    if (hasNestedGridPattern) {
                        // Render as a single row with columns for each top-level item
                        return (
                            <Row
                                gutter={[20, 20]}
                                className="mt-4"
                                key={`nested-row-${keyPrefix}`}
                                style={{ height: "312px" }}
                            >
                                {structure.map((item, index) => {
                                    if (Array.isArray(item)) {
                                        // Check if this item contains nested arrays (like [[w1,w2],[w3,w4]])
                                        const isNestedGrid = item.some(subItem => Array.isArray(subItem));
                                        
                                        if (isNestedGrid) {
                                            // This is a nested grid - render in a column with 2/3 width
                                            return (
                                                <Col span={16} key={`nested-grid-${keyPrefix}-${index}`}>
                                                    {item.map((subRow, subIndex) => (
                                                        <Row 
                                                            gutter={[20, 20]} 
                                                            className={subIndex > 0 ? "mt-3" : ""} 
                                                            key={`sub-row-${keyPrefix}-${index}-${subIndex}`}
                                                        >
                                                            {subRow.map((widget: any, widgetIndex: number) => (
                                                                <Col xs={24} md={12} key={`${widget.type}-${widgetIndex}`}>
                                                                    <widget.widgetComponent
                                                                        title={widget.title}
                                                                        subTitle={widget?.subTitle || ""}
                                                                        infoMessage={widget.infoMessage || ""}
                                                                        config={widget.config}
                                                                        {...widget.formatter(widget, widgetData)}
                                                                    />
                                                                </Col>
                                                            ))}
                                                        </Row>
                                                    ))}
                                                </Col>
                                            );
                                        } else {
                                            // This is a simple array of widgets - render in a column with 1/3 width
                                            return (
                                                <Col span={8} className="h-100" key={`simple-array-${keyPrefix}-${index}`}>
                                                    {item.map((widget: any, widgetIndex: number) => {
                                                        const componentData = widget.formatter(widget, widgetData);
                                                        return (
                                                            <div key={`widget-container-${widgetIndex}`} className={`h-100 ${widgetIndex > 0 ? "mt-3" : ""}`}>
                                                                <widget.widgetComponent
                                                                    title={widget.title}
                                                                    subTitle={widget?.subTitle || ""}
                                                                    infoMessage={widget.infoMessage || ""}
                                                                    config={widget.config}
                                                                    {...componentData}
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                                </Col>
                                            );
                                        }
                                    } else if (item && typeof item === 'object' && item.type) {
                                        // Single widget
                                        const colSpan = 24 / structure.length;
                                        return (
                                            <Col span={colSpan} key={`single-widget-${keyPrefix}-${index}`}>
                                                {renderWidget(item, index)}
                                            </Col>
                                        );
                                    }
                                    return null;
                                })}
                            </Row>
                        );
                    } else {
                        // This is a regular nested structure - recurse further
                        return structure.map((item: any, index: number) =>
                            renderWidgetStructure(item, `${keyPrefix}-${index}`)
                        );
                    }
                }
            } else if (structure && typeof structure === 'object' && structure.type) {
                // This is a single widget
                return renderWidget(structure, 0);
            }
            return null;
        },
        [renderWidget]
    );

    const renderedWidgets = useMemo(() => {
        if (!widgetConfig?.widgets) return null;
        return renderWidgetStructure(widgetConfig.widgets);
    }, [widgetConfig?.widgets, renderWidgetStructure]);

    // Render with WidgetCard wrapper for non-drilldown types
    if (widgetConfig?.config?.widgetCard?.type !== "drilldown") {
        return (
            <WidgetCard
                className="w-100 my-3"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <div className="d-flex justify-content-between align-items-center">
                    <h4>{title || "Performance Overview"}</h4>
                    {widgetConfig?.config?.isTrendsButtonRequired &&
                        buildWidgetCardFilters({
                            filters: [
                                {
                                    section: "" as const,
                                    type: RevstackFilterTypes.TRENDS,
                                    isComingSoon: widgetConfig?.config?.isTrendsButtonComingSoon,
                                    props: {
                                        onClick: () => {
                                            trendsClickHandler({
                                                widgetConfig,
                                                titlePills: [],
                                                selectedFilterOptions,
                                                secondaryFilters: [{
                                                    filterKey: CommonWidgetMetrics.CATEGORY,
                                                    filterDataKey: `${CommonWidgetMetrics.CATEGORY}_id`,
                                                    filterValue: ["all"],
                                                    relatedColDataKey: CommonWidgetMetrics.CATEGORY,
                                                    allOptions: {
                                                        label: "All Categories",
                                                        value: OptionValues.ALL
                                                    }
                                                }, {
                                                    filterKey: `secondary_${CommonWidgetMetrics.REGION}`,
                                                    filterDataKey: CommonWidgetMetrics.REGION,
                                                    filterValue: ["all"],
                                                    fetchOptions: true,
                                                    requestParams: {
                                                        cubeId: RevstackCubeIds.PO_REGIONAL,
                                                        queryId: "any-id-can-be-added",
                                                        queryDimensions: [RevstackFilterKey.REGION],
                                                        isExcludeDimension: false,
                                                        excludeFilter: [RevstackFilterKey.BRAND],
                                                        filterToSkip: ""
                                                    },
                                                    allOptions: { label: "National", value: OptionValues.ALL },
                                                }],
                                            });

                                        },
                                    },
                                },
                            ]
                        })
                    }
                </div>
                {renderedWidgets}
            </WidgetCard>
        );
    }

    // Render without wrapper for drilldown type
    return <>{renderedWidgets}</>;
};

export default RevstackPoCategoryAnalysisWidget;
